package com.neurobs.ccabserver.transcriptreview.util

import com.neurobs.ccab.casr.CasrDatabaseJSON.{DatabaseTranscriptJSON, DatabaseWordJSON, EngineTranscriptJSON, EngineWordJSON, ReviewedTestTranscriptJSON, ReviewedTestTranscriptRecordingJSON, ReviewedTranscriptJSON, ReviewedWordJSON}
import com.neurobs.ccab.casr.EngineIdentifiers
import com.neurobs.ccabserver.transcriptreview.util.EditableTranscript.wordInsertionHelper
import com.neurobs.util.TimeSeconds
import com.neurobs.util.TimeSeconds.TimeSecondsInterval
import org.scalajs.dom.console

trait DatabaseTranscriptInterface {
	val replacementDuration: TimeSeconds = TimeSeconds(0.2)
	val noValueSentinel = TimeSeconds(-999)
	def processTranscriptWordJson[T <: DatabaseWordJSON](words: Seq[T], interval: TimeSecondsInterval): Seq[T] = {
		var lastEndTime: TimeSeconds = interval.begin
		val nonEmpty = words.filterNot(_.isEmpty)
		nonEmpty
			.zipWithIndex
			//.filter(word => word.startTime().nonNegative() && word.endTime().nonNegative())
			.map { e =>
				val (w, i) = e
				val (tempStart, tempEnd) = {
					if (w.startTime() == noValueSentinel) (lastEndTime, lastEndTime + replacementDuration)
					else if (w.endTime() == noValueSentinel) (w.startTime(), w.startTime() + replacementDuration)
					else if (w.duration().negative()) (w.startTime(), w.startTime() + replacementDuration)
					else (w.startTime(), w.endTime())
				}
				val newWord =
					if ((tempStart < lastEndTime) || (tempStart > interval.end)) w.newTimes(lastEndTime, lastEndTime + (tempEnd - tempStart))
					else if (i + 1 >= nonEmpty.size) w.newTimes(tempStart, tempEnd)
					else {
						val nextWord = nonEmpty(i + 1)
						if ((nextWord.startTime() == noValueSentinel) || (nextWord.startTime() >= tempStart) || (nextWord.startTime() < lastEndTime)) w.newTimes(tempStart, tempEnd)
						else {
							w.newTimes(lastEndTime, lastEndTime + (tempEnd - tempStart))
						}
					}
				lastEndTime = newWord.endTime()
				newWord.asInstanceOf[T]
			}
//      .sortBy(_.startTime())
	}
}

abstract trait Transcript[T <: TranscriptWord] {
	def transcriptWords: Seq[T]
}

class EditableTranscript(editableTranscriptWords: Seq[EditableTranscriptWord]) extends Transcript[EditableTranscriptWord] {

	var transcriptWords = editableTranscriptWords


	def addWord(newWord: EditableTranscriptWord) = {
		transcriptWords = wordInsertionHelper(newWord, transcriptWords.toList)
	}

	def removeWord(word: EditableTranscriptWord) = {
		transcriptWords = transcriptWords.filterNot(_ == word)
	}

	def findCorrespondingWord(word: TranscriptWord, mutualOverlapFractionThresh: Double = .7): Option[EditableTranscriptWord] = {
		transcriptWords.find(_.correspondsTo(word, mutualOverlapFractionThresh))
	}

	def toReviewedTestTranscriptRecordingJson(audioFileCode: String, recordingOffset: TimeSeconds, username: String, latency: Boolean): ReviewedTestTranscriptRecordingJSON = {
		val reviewedTranscriptJson = ReviewedTranscriptJSON(eid = EngineIdentifiers.REVIEWED, u = username, l = latency, ws = transcriptWords.map(_.toReviewedWordJson))
		ReviewedTestTranscriptRecordingJSON(audioFileCode, recordingOffset, reviewedTranscriptJson)
	}
}

object EditableTranscript extends DatabaseTranscriptInterface {
	def apply(transcriptJson: DatabaseTranscriptJSON, interval: TimeSecondsInterval): EditableTranscript = {
		new EditableTranscript(
			processTranscriptWordJson(transcriptJson.words(), interval).map(EditableTranscriptWord(_))
		)
	}

	def apply(): EditableTranscript = new EditableTranscript(Seq())

	def apply(transcript: NonEditableTranscript): EditableTranscript = {
		new EditableTranscript(transcript.nonEditableTranscriptWords.map(EditableTranscriptWord(_)))
	}

	protected def wordInsertionHelper(newWord: EditableTranscriptWord, existingWords: List[EditableTranscriptWord]): List[EditableTranscriptWord] = {
		existingWords.headOption match {
			case None => List(newWord)
			case Some(headWord) =>
				if (newWord.startTime < headWord.startTime) newWord :: existingWords
				else headWord :: wordInsertionHelper(newWord, existingWords.tail)
		}
	}

}

case class NonEditableTranscript(engineIdentifier: String, nonEditableTranscriptWords: Seq[NonEditableTranscriptWord]) extends Transcript[NonEditableTranscriptWord] {
	override def transcriptWords: Seq[NonEditableTranscriptWord] = nonEditableTranscriptWords
}

object NonEditableTranscript extends DatabaseTranscriptInterface {
	def apply(transcriptJson: EngineTranscriptJSON, interval: TimeSecondsInterval): NonEditableTranscript = {
		NonEditableTranscript(
			transcriptJson.engineIdentifier(),
			processTranscriptWordJson(transcriptJson.words(), interval).map(NonEditableTranscriptWord(_))
		)
	}
	def apply(json: ReviewedTestTranscriptRecordingJSON, interval: TimeSecondsInterval): NonEditableTranscript = {
		NonEditableTranscript(
			json.reviewedTranscript().username(),
			processTranscriptWordJson(json.reviewedTranscript().words(), interval).map(NonEditableTranscriptWord.fromDatabaseWord(_))
		)
	}

	def apply(): NonEditableTranscript = NonEditableTranscript("", Seq())
}

trait TranscriptWord {
	def word: String
	def startTime: TimeSeconds
	def endTime: TimeSeconds

	private def roundTime(time: TimeSeconds, decimalPlaces: Int): TimeSeconds = {
		if (decimalPlaces < 0) time
		else {
			val tenToPowerOfPrecision = math.pow(10, decimalPlaces)
			TimeSeconds(math.rint(time.seconds() * tenToPowerOfPrecision)/tenToPowerOfPrecision)
		}
	}

	def roundedStartTime(decimalPlaces: Int): TimeSeconds = roundTime(startTime, decimalPlaces)
	def roundedEndTime(decimalPlaces: Int): TimeSeconds = roundTime(endTime, decimalPlaces)

	def duration: TimeSeconds = endTime - startTime

	def overlapSecondsWith(otherWord: TranscriptWord): TimeSeconds = {
		(endTime.min(otherWord.endTime) - (startTime).max(otherWord.startTime)).max(TimeSeconds(0))
	}

	def overlapFractionWith(otherWord: TranscriptWord): Double = {
		overlapSecondsWith(otherWord).seconds() / duration.seconds()
	}

	def correspondsTo(otherWord: TranscriptWord, mutualOverlapFractionThresh: Double): Boolean = {
		overlapFractionWith(otherWord) >= mutualOverlapFractionThresh &&
			otherWord.overlapFractionWith(this) >= mutualOverlapFractionThresh
	}

}

class EditableTranscriptWord(initialWord: String, initialStartTime: TimeSeconds, initialEndTime: TimeSeconds) extends TranscriptWord {

	var word = initialWord
	var startTime = initialStartTime
	var endTime = initialEndTime

	def getEndTime: TimeSeconds = endTime

	def editWord(newWord: String): Unit = {
		word = newWord
	}

	def editStartTime(deltaSeconds: TimeSeconds): Unit = {
		startTime = startTime + deltaSeconds
	}

	def toReviewedWordJson: ReviewedWordJSON = {
		ReviewedWordJSON(word, startTime, endTime)
	}
}

object EditableTranscriptWord {
	def apply(word: DatabaseWordJSON): EditableTranscriptWord = {
		new EditableTranscriptWord(initialWord = word.word(), initialStartTime = word.startTime(), initialEndTime = word.endTime())
	}

	def apply(): EditableTranscriptWord = {
		new EditableTranscriptWord(initialWord = "", initialStartTime = TimeSeconds(0), initialEndTime = TimeSeconds(0))
	}

	def apply(word: NonEditableTranscriptWord): EditableTranscriptWord = {
		new EditableTranscriptWord(initialWord = word.word, initialStartTime = word.startTime, initialEndTime = word.endTime)
	}
}

case class NonEditableTranscriptWord(word: String, startTime: TimeSeconds, endTime: TimeSeconds, confidence: Double) extends TranscriptWord

object NonEditableTranscriptWord {
	def apply(wordJson: EngineWordJSON): NonEditableTranscriptWord = {
		NonEditableTranscriptWord(wordJson.word(), wordJson.startTime(), wordJson.endTime(), wordJson.confidence())
	}
	def fromDatabaseWord(wordJson: DatabaseWordJSON): NonEditableTranscriptWord = {
		NonEditableTranscriptWord(wordJson.word(), wordJson.startTime(), wordJson.endTime(), 1)
	}

	def apply(): NonEditableTranscriptWord = {
		NonEditableTranscriptWord("", TimeSeconds(0), TimeSeconds(0), 0)
	}

}


